import { Kbd } from "@heroui/kbd";
import { SidebarLeft01Icon, SidebarRight01Icon } from "@icons";
import type { ReactNode } from "react";
import { SidebarHeaderButton } from "@/components/layout/headers/SidebarHeaderButton";
import ContactSupport from "@/components/layout/sidebar/ContactSupport";
import SidebarTopButtons from "@/components/layout/sidebar/SidebarTopButtons";
import UserContainer from "@/components/layout/sidebar/UserContainer";
import { LogoWithContextMenu } from "@/components/shared/LogoWithContextMenu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { WhatsNewCard } from "@/features/whats-new/components/WhatsNewCard";
import { usePlatform } from "@/hooks/ui/usePlatform";
import { useElectron } from "@/hooks/useElectron";
import { ANALYTICS_EVENTS, trackEvent } from "@/lib/analytics";

interface SidebarLayoutProps {
  children: ReactNode;
}

// Custom SidebarTrigger with dynamic icons
export const CustomSidebarTrigger = () => {
  const { open, toggleSidebar } = useSidebar();
  const { isMac } = usePlatform();

  const handleToggle = () => {
    trackEvent(
      open
        ? ANALYTICS_EVENTS.UI_SIDEBAR_COLLAPSED
        : ANALYTICS_EVENTS.UI_SIDEBAR_EXPANDED,
    );
    toggleSidebar();
  };

  return (
    <SidebarHeaderButton
      onClick={handleToggle}
      aria-label="Toggle Sidebar"
      tooltip={
        <span className="flex items-center gap-2 text-xs">
          {open ? "Collapse Sidebar" : "Open Sidebar"}
          <div>
            <Kbd className="text-xs rounded-md">{isMac ? "⌘" : "Ctrl"}</Kbd>
            <Kbd className="text-xs rounded-md">B</Kbd>
          </div>
        </span>
      }
    >
      {open ? (
        <SidebarLeft01Icon className="max-h-5 min-h-5 max-w-5 min-w-5 text-zinc-500 transition group-hover/btn:text-primary" />
      ) : (
        <SidebarRight01Icon className="max-h-5 min-h-5 max-w-5 min-w-5 text-zinc-500 transition group-hover/btn:text-primary" />
      )}
    </SidebarHeaderButton>
  );
};

export default function SidebarLayout({ children }: SidebarLayoutProps) {
  const { isElectron } = useElectron();
  const { isMac } = usePlatform();

  return (
    <Sidebar
      variant="sidebar"
      collapsible="offcanvas"
      className="sidebar border-none!"
    >
      {/* In the desktop app the window chrome owns the top-left corner:
          no logo, and on macOS extra top padding clears the traffic
          lights (hiddenInset title bar, lights at y=16). */}
      <SidebarHeader className={isElectron && isMac ? "pt-10 pb-0" : "pb-0"}>
        {!isElectron && (
          <div className="flex items-center justify-between">
            <LogoWithContextMenu className="group ml-2 flex items-center gap-2 px-1" />
          </div>
        )}
      </SidebarHeader>

      <SidebarContent className="flex-1 px-1">
        <SidebarGroup>
          <SidebarGroupContent className="space-y-1 overflow-hidden">
            <SidebarTopButtons />
            {children}
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="relative z-4 p-2 px-3 pb-3">
        <WhatsNewCard />
        <UserContainer />
        <ContactSupport />
      </SidebarFooter>
    </Sidebar>
  );
}
